// MessageQueue — طابور الرسائل (3.4.28): حالة التسليم + إعادة محاولة + اختبار.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const STATUSES = ['pending', 'sending', 'sent', 'failed', 'cancelled'];
const statusColor = { sent: '#2e7d32', failed: '#c62828', pending: '#b8860b', sending: '#1a468d', cancelled: '#777' };

export default function MessageQueue() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [test, setTest] = useState({ channel: 'log', recipient: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/messaging/queue', { params: { status: statusFilter || undefined } });
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError(''); setInfo('');
    try { const r = await fn(); if (r?.data) setInfo(JSON.stringify(r.data)); load(); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
  };

  const counts = useMemo(() => rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}), [rows]);

  return (
    <SmartPage>
      <SmartHeader title={t('message_queue', 'طابور الرسائل')} subtitle={t('message_queue_hint', 'تتبّع تسليم الرسائل، وإعادة محاولة الفاشلة منها.')} />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {info ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert> : null}

      {/* اختبار سريع */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label={t('channel', 'القناة')} value={test.channel} onChange={(e) => setTest((s) => ({ ...s, channel: e.target.value }))} sx={{ width: 150 }}>
          <MenuItem value="log">log</MenuItem>
          <MenuItem value="email">email</MenuItem>
          <MenuItem value="whatsapp">whatsapp</MenuItem>
        </TextField>
        <TextField size="small" label={t('recipient', 'المستلِم')} value={test.recipient} onChange={(e) => setTest((s) => ({ ...s, recipient: e.target.value }))} sx={{ width: 240 }} />
        <Button startIcon={<SendIcon />} variant="contained" onClick={() => act(() => api.post('/messaging/test', test))}
          sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
          {t('send_test', 'إرسال اختبار')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<PlayArrowIcon />} variant="outlined" onClick={() => act(() => api.post('/messaging/process'))}>{t('process_now', 'معالجة الآن')}</Button>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>{t('refresh', 'تحديث')}</Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label={`${t('all', 'الكل')}`} color={!statusFilter ? 'primary' : 'default'} onClick={() => setStatusFilter('')} clickable />
        {STATUSES.map((s) => (
          <Chip key={s} label={`${t(s, s)} ${counts[s] ? `(${counts[s]})` : ''}`} clickable
            color={statusFilter === s ? 'primary' : 'default'} onClick={() => setStatusFilter(s)} />
        ))}
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: C.cardSoft }}>
                {['#', t('channel', 'القناة'), t('recipient', 'المستلِم'), t('subject', 'الموضوع'), t('status', 'الحالة'), t('attempts', 'المحاولات'), t('last_error', 'آخر خطأ'), ''].map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 900 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: C.muted, py: 3 }}>{t('no_messages', 'لا رسائل')}</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.message_id}>
                  <TableCell>{r.message_id}</TableCell>
                  <TableCell>{r.channel}</TableCell>
                  <TableCell>{r.recipient}</TableCell>
                  <TableCell>{r.subject || '—'}</TableCell>
                  <TableCell><Chip size="small" label={t(r.status, r.status)} sx={{ bgcolor: statusColor[r.status] || C.line, color: '#fff', fontWeight: 800 }} /></TableCell>
                  <TableCell>{r.attempts}/{r.max_attempts}</TableCell>
                  <TableCell sx={{ maxWidth: 240, color: C.rose, fontSize: 12 }}>{r.last_error || '—'}</TableCell>
                  <TableCell align="right">
                    {['failed', 'pending'].includes(r.status) ? (
                      <>
                        <Tooltip title={t('retry', 'إعادة المحاولة')}><IconButton size="small" onClick={() => act(() => api.post(`/messaging/${r.message_id}/retry`))}><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title={t('cancel', 'إلغاء')}><IconButton size="small" onClick={() => act(() => api.post(`/messaging/${r.message_id}/cancel`))}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </SmartPage>
  );
}
